import { TaxiCompanyList } from '../../../../../services/taxi/GetDirectionAndFare/models/getFareSamplesParams';
import { parseFromyyyyMMddHHmm, toLuxonDateTime, getNowLuxonDateTime } from '../../../../../utils/luxonUtil';
import Environment from '../../../../../utils/environment';

/**
 * 空港送迎マイル増量キャンペーンの設定。
 *
 * ◤ 恒久モジュールの考え方 ◢
 * 本モジュールは「作っては削除」を繰り返さず、常設のまま運用する。
 * キャンペーンの有効/無効は {@link MileageCampaignConfig.start}〜{@link MileageCampaignConfig.end}
 * による日付判定で自動的に切り替わるため、デプロイのタイミングで表示ON/OFFを管理する必要はない。
 *
 * 次回キャンペーン時は、新規にコードを書き起こすのではなく
 * {@link CURRENT_MILEAGE_CAMPAIGN} の値（期間・対象会社・文言・PDF配置）を上書きするだけでよい。
 * 過去のキャンペーン設定を残しても、期間が過去になるため自動的に無効化される。
 */
export interface MileageCampaignConfig {
  /** 管理用ID（チケット番号など。ログ・判別用） */
  id: string;
  /** 開始日時。操作時刻（現在時刻）がこれ以降であること。ISO8601（+09:00）形式 */
  start: string;
  /** 終了日時。乗車日時がこれ以前であること。ISO8601（+09:00）形式 */
  end: string;
  /** キャンペーン対象の全会社。期間中はこれらでAMC（マイレージ番号）入力欄を表示する */
  targetCompanies: TaxiCompanyList[];
  /** 通常マイルあり（元々AMC ON）の会社。PDF①（通常マイルあり用）の対象 */
  originalSupportedCompanies: TaxiCompanyList[];
  /**
   * 空港限定で通常マイルありとなる会社。
   * 例: イースタン（limoTaxi）は羽田・成田のみ通常マイルあり。
   */
  airportLimitedOriginalSupported: { company: TaxiCompanyList; airports: string[] }[];
  /** PDF配置ディレクトリ（public配下。例: 'AM-1152'） */
  pdfDir: string;
  /** 注意事項・案内テキストで使う可変文言 */
  text: {
    /** 案内テキストの見出し（例: ＼空港タクシーでWマイルキャンペーン実施中／） */
    leadText: string;
    /** 付与マイルのレート（例: 200円（税込）につき2マイル） */
    mileRate: string;
    /** ボーナスマイルの積算時期（例: 2027年4〜5月頃） */
    bonusAccrualTiming: string;
  };
}

/**
 * 【AM-1152】空港送迎 Wマイルキャンペーン（2026/11/5〜2027/2/26）
 *
 * 対象会社・通常マイルあり/なしの区分はANA提供のExcel（依頼先=VALの行）に準拠。
 * ★モーダル文言追加・タイル画像追加・キャンペーンお問い合わせ番号はBIP（ANA公式サイト側）
 * 担当のため本リポジトリの対象外。
 */
export const CURRENT_MILEAGE_CAMPAIGN: MileageCampaignConfig = {
  /* 管理用ID（チケット番号など。ログ・判別用） */
  id: 'AM-1152',
  /* 開始日時 */
  start: '2026-11-05T00:00:00+09:00',
  /* 終了日時 */
  end: '2027-02-26T23:59:59+09:00',
  /* キャンペーン対象会社のリスト */
  targetCompanies: [
    /** イースタン交通（羽田・成田） */
    TaxiCompanyList.limoTaxi,
    /** イースタン交通（その他エリア） */
    TaxiCompanyList.eastern,
    /** 網走ハイヤー */
    TaxiCompanyList.abashiri,
    /** 旭川中央タクシー */
    TaxiCompanyList.Asahikawachuuo,
    /** 函館タクシー */
    TaxiCompanyList.hakodateTaxi,
    /** キングタクシー */
    TaxiCompanyList.kingTaxi,
    /** キングタクシー2 */
    TaxiCompanyList.kingTaxi2,
    /** 秋田エアポートライナー */
    TaxiCompanyList.airport_liner,
    /** にかほGO */
    TaxiCompanyList.nikaho_go,
    /** 稲荷タクシー */
    TaxiCompanyList.inari,
    /** 稲荷タクシー（高速料金） */
    TaxiCompanyList.inarihw,
    /** 庄交ハイヤー */
    TaxiCompanyList.shokoHire,
    /** 酒田タクシー */
    TaxiCompanyList.sakataNo1Taxi,
    /** 大和自動車交通 */
    TaxiCompanyList.daiwaj,
    /** 北福島タクシー */
    TaxiCompanyList.kitafukushima,
    /** 磐城タクシー */
    TaxiCompanyList.iwakiTaxi,
    /** 石川交通 */
    TaxiCompanyList.ishikawakotsu,
    /** 伊予鉄タクシー（定額） */
    TaxiCompanyList.iyotetsutaxi,
    /** 伊予鉄タクシー（メーター料金） */
    TaxiCompanyList.iyotetsuTaxi2,
    /** 土電ハイヤー */
    TaxiCompanyList.toden,
    /** 宇部山電タクシー */
    TaxiCompanyList.ubeSanden,
    /** 宇部構内タクシー */
    TaxiCompanyList.ubeKounai,
    /** 通津タクシー */
    TaxiCompanyList.tsuzutaxi,
    /** 日ノ丸ハイヤー（鳥取・米子） */
    TaxiCompanyList.hinomaru,
    /** 大分別府合同タクシー */
    TaxiCompanyList.gotaku,
    /** 宮交タクシー */
    TaxiCompanyList.miyakou,
    /** 那覇ハイヤー */
    TaxiCompanyList.nahahire,
    /** つばめタクシー */
    TaxiCompanyList.tsubame,
    /** つばめハイヤー */
    TaxiCompanyList.tsubamelimo,
    /** 北鹿観光ハイヤー */
    TaxiCompanyList.hokuroku,
  ],
  /* 通常マイルありの会社のリスト */
  originalSupportedCompanies: [
    TaxiCompanyList.Asahikawachuuo,
    TaxiCompanyList.hakodateTaxi,
    TaxiCompanyList.iyotetsutaxi,
    TaxiCompanyList.iyotetsuTaxi2,
    TaxiCompanyList.toden,
    TaxiCompanyList.hokuroku,
  ],
  /* 空港限定で通常マイルありの会社のリスト */
  airportLimitedOriginalSupported: [
    /** イースタン（limoTaxi）は羽田・成田のみ通常マイルあり */
    { company: TaxiCompanyList.limoTaxi, airports: ['HND', 'NRT'] },
  ],
  /* キャンペーン説明PDFのディレクトリ */
  pdfDir: 'AM-1152',
  /* キャンペーン説明文 */
  text: {
    leadText: '＼空港タクシーでWマイルキャンペーン実施中／',
    mileRate: '200円（税込）につき2マイル',
    bonusAccrualTiming: '2027年4月〜5月頃',
  },
};

/**
 * 指定した会社がキャンペーン対象か。
 */
export const isMileageCampaignTargetCompany = (
  company: TaxiCompanyList,
  campaign: MileageCampaignConfig = CURRENT_MILEAGE_CAMPAIGN,
): boolean => campaign.targetCompanies.includes(company);

/**
 * キャンペーン期間中か判定する。
 * - 開始: 操作時刻（現在時刻）が {@link MileageCampaignConfig.start} 以降
 * - 終了: 乗車日時が {@link MileageCampaignConfig.start}〜{@link MileageCampaignConfig.end} の範囲内
 *
 * コードを常設したまま日付で自動的にON/OFFされるため、本番反映タイミングに依存しない。
 *
 * @param rideDatetime 乗車日時（yyyyMMddHHmm形式）
 */
export const isMileageCampaignActive = (
  rideDatetime: string | undefined,
  campaign: MileageCampaignConfig = CURRENT_MILEAGE_CAMPAIGN,
): boolean => {
  if (!rideDatetime) return false;

  const now = getNowLuxonDateTime();
  const campaignStart = toLuxonDateTime(campaign.start);

  // 開始前なら false
  if (now < campaignStart) return false;

  const rideDate = parseFromyyyyMMddHHmm(rideDatetime);
  const campaignEnd = toLuxonDateTime(campaign.end);

  // 乗車日時が期間外なら false
  if (rideDate < campaignStart || rideDate > campaignEnd) return false;

  return true;
};

/**
 * 通常マイルあり（キャンペーンに関係なく元々AMC ON）の会社/空港か。
 * PDF①（通常マイルあり用）か②（上記以外用）かの出し分けにも利用する。
 *
 * @param airport 空港レター（例: 'HND'）。空港限定会社の判定に使用
 */
export const isMileageCampaignOriginalSupported = (
  company: TaxiCompanyList,
  airport: string | undefined,
  campaign: MileageCampaignConfig = CURRENT_MILEAGE_CAMPAIGN,
): boolean => {
  const airportLimited = campaign.airportLimitedOriginalSupported.find((c) => c.company === company);
  if (airportLimited) {
    return airportLimited.airports.includes(airport || '');
  }
  return campaign.originalSupportedCompanies.includes(company);
};

/**
 * PDFで利用するHOSTの一覧。
 * ⚠️ 注意: PDFのリンクは必ず絶対URLで指定すること。
 * 相対パスにすると旅CUBE(tabicube.com)ホスト経由でアクセスした際に、
 * PDFが存在しない tabicube.com 側を参照して404になる。
 */
const PDF_HOST_LIST = {
  stg: 'https://ana-maas-staging.val.jp',
  prod: 'https://ana-maas.val.jp',
};

/** PDFで利用するHOST（本番のみprod、それ以外はstg）。env判定は既存の Environment util を利用 */
const PDF_HOST = Environment.isProduction(process.env['REACT_APP_STAGE_ENV'] || '') ? PDF_HOST_LIST.prod : PDF_HOST_LIST.stg;

/**
 * キャンペーン説明PDFのURL（絶対URL）。
 * 旅CUBE等ホストが変わっても参照できるよう、必ず絶対URLで返す（相対パスは404になるため不可）。
 */
export const getMileageCampaignPdfUrl = (
  isOriginalSupported: boolean,
  campaign: MileageCampaignConfig = CURRENT_MILEAGE_CAMPAIGN,
): string => `${PDF_HOST}/${campaign.pdfDir}/mileage-campaign-${isOriginalSupported ? 1 : 2}.pdf`;
